import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { resolveAssetUuidFromRegistryId } from '../registries/resolveVoiceoverId';
import { VoiceoverRegistry } from '../registries/VoiceoverRegistry';
import type { ToolExecutionContext } from '../types';

import { newSceneGroupBaseSceneNote } from './base-scene-note';
import { ConversationTool } from './ConversationTool';
import { computeSceneGroupShortIds, resolveInsertSlotAfter } from './scene-group-addressing';

import { findWordSequenceIndices } from './transcript-word-match';

import { applyPlaybackRegionsToSceneGroupOperation } from 'js/src/data/clip-creator/clip-creator-operations';
import type { ImmutableAudioAsset } from 'js/src/data/media';
import { checkpointVideo } from 'js/src/data/video-data';
import type { RootState } from 'js/src/libs/RootState';
import {
    createNewVoicedrivenSceneGroupOperation,
    deleteSceneGroupOperation,
    saveRawLuminary,
    setAndApplyVoiceoverAssetForSceneGroupOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const addRecordedVoiceoverSceneGroupSchema = z.object({
    after_scene_group_id: z
        .string()
        .optional()
        .describe(
            'The stable scene_group_id of the scene group the new voiceover scene group should be inserted immediately after. Omit to insert it at the very start of the video.',
        ),
    audio_id: z.string().describe('The id of the uploaded voiceover audio file.'),
    language_code: z
        .string()
        .optional()
        .describe(
            'Optional language code for caption generation (e.g., "en", "es"). Auto-detected if omitted.',
        ),
    words: z
        .array(z.string())
        .min(1)
        .optional()
        .describe(
            'Include only part of the recording: the exact transcript words of the segment to keep, in order (e.g. from a clip_select answer). The segment spans those words, so it starts and ends on a word boundary. Omit to include the whole recording.',
        ),
});
export type AddRecordedVoiceoverSceneGroupArgs = z.infer<
    typeof addRecordedVoiceoverSceneGroupSchema
>;

export class AddRecordedVoiceoverSceneGroupTool extends ConversationTool<
    typeof addRecordedVoiceoverSceneGroupSchema
> {
    protected override readonly schema = addRecordedVoiceoverSceneGroupSchema;
    protected override readonly toolName = TOOL_NAMES.addRecordedVoiceoverSceneGroup;
    protected override readonly toolDescription =
        'Add a new voiceover scene group driven by an uploaded voiceover AUDIO file (e.g. a human recording). The audio becomes the narration track and its speech is transcribed into a synced transcript.';

    public override getToolDisplayName(): string {
        return 'Adding voiceover scene group';
    }

    public get statusIcon(): string {
        return 'mic';
    }

    public async execute(args: AddRecordedVoiceoverSceneGroupArgs, context: ToolExecutionContext) {
        const { after_scene_group_id, audio_id, language_code, words } = args;
        const { parentActionGroup } = context;

        if (words && words.length === 0) {
            return `Error: "words" was empty. Pass the transcript words of the segment to keep, or omit it to include the whole recording.`;
        }

        // Resolve the insert slot up front (fail-closed on an unknown anchor id).
        const slot = resolveInsertSlotAfter(this.luminary, after_scene_group_id);

        const voiceoverRegistry = VoiceoverRegistry.getInstance();

        // The id resolves straight to its audio asset, covering both sources the agent has:
        // a chat attachment and a voiceover search result.
        const voiceoverAsset: ImmutableAudioAsset | null =
            voiceoverRegistry.getAudioAssetData(audio_id);

        if (!voiceoverAsset) {
            // A media id resolves too — that means a video (e.g. a talking head), which is
            // another tool's job. Name it so the agent redirects instead of retrying blindly.
            if (resolveAssetUuidFromRegistryId(audio_id)) {
                return `Error: ${audio_id} is not an audio file. For an uploaded talking-head video, use ${TOOL_NAMES.addTalkingHeadSceneGroup} instead.`;
            }
            return `Error: Could not find an uploaded audio file with id ${audio_id}. Make sure the audio file was attached to the chat and finished uploading, or was returned by ${TOOL_NAMES.searchUploadedVoiceovers}.`;
        }
        if (!voiceoverAsset.get('has_voiceover')) {
            return `Error: The uploaded audio does not appear to contain spoken voiceover. Please upload an audio file with clear speech.`;
        }

        try {
            const {
                createNewVoicedrivenSceneGroup,
                setAndApplyVoiceoverAssetForSceneGroup,
                deleteSceneGroup,
                applyPlaybackRegionsToSceneGroup,
            } = makeOperationsCallable(
                {
                    createNewVoicedrivenSceneGroupOperation,
                    setAndApplyVoiceoverAssetForSceneGroupOperation,
                    deleteSceneGroupOperation,
                    applyPlaybackRegionsToSceneGroupOperation,
                },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );

            const { sceneGroupId } = createNewVoicedrivenSceneGroup(
                VideoData.CATEGORY_VOICEOVER,
                slot,
                { deleteOtherScenes: false, setPlaceholderAsset: false, hasAvatar: false },
                parentActionGroup,
                false,
            );

            // Applies the uploaded audio as the voiceover. The transcript is omitted so
            // the operation fetches (or generates) it from the audio asset itself.
            // recommendOverlays is false — the agent adds overlays explicitly via
            // add_voiceover_scene. This operation checkpoints the video itself.
            const hasAssetBeenApplied = await setAndApplyVoiceoverAssetForSceneGroup({
                sceneGroupId,
                audioAssetData: voiceoverAsset,
                avatarAssetData: null,
                captionsLanguage: language_code ?? null,
                forceCaptionGeneration: false,
                selectedBlueprintId: null,
                generateRecommendedVideoResponse: null,
                sceneRecommendations: null,
                recommendOverlays: false,
                isGlobalVideo: true,
                systemTriggered: false,
                parentActionGroup,
            });

            const cleanupSceneGroup = (): boolean => {
                try {
                    deleteSceneGroup(sceneGroupId, false, parentActionGroup);
                    return true;
                } catch {
                    return false;
                }
            };

            if (!hasAssetBeenApplied) {
                cleanupSceneGroup();
                return `Error: the voiceover audio could not be applied to the new scene group.`;
            }

            let keptWordSpan: { startTimeMs: number; endTimeMs: number } | undefined;
            if (words) {
                const createdSceneGroup = this.luminary.getSceneGroupById(sceneGroupId);
                const transcript = createdSceneGroup?.originalTranscript;
                const transcriptWords =
                    transcript?.transcriptData?.words || transcript?.words || [];
                const match = createdSceneGroup
                    ? findWordSequenceIndices(transcriptWords, words)
                    : null;
                if (!match || !createdSceneGroup) {
                    const removed = cleanupSceneGroup();
                    const quoted = `"${words.join(' ')}"`;
                    return removed
                        ? `Error: ${quoted} does not appear in this recording's transcript, so there is no segment to keep. Nothing was added. Copy the words exactly as they appear in the transcript.`
                        : `Error: ${quoted} does not appear in this recording's transcript. The scene group was added covering the full recording and is the only one in the video, so it could not be removed automatically — replace it or retry with words from the transcript.`;
                }
                keptWordSpan = {
                    startTimeMs: transcriptWords[match.startIndex].startTime,
                    endTimeMs: transcriptWords[match.endIndex].endTime,
                };
                applyPlaybackRegionsToSceneGroup(createdSceneGroup, [
                    [keptWordSpan.startTimeMs, keptWordSpan.endTimeMs],
                ]);
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            this.startBaseSceneBuilding(sceneGroupId);

            const durationSeconds = voiceoverAsset.get('duration');
            const durationInfo =
                typeof durationSeconds === 'number' && !keptWordSpan
                    ? ` Voiceover duration: ${durationSeconds.toFixed(1)}s.`
                    : '';

            const shortId =
                computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                    sceneGroupId,
                ) ?? sceneGroupId;
            const placement =
                after_scene_group_id && after_scene_group_id.trim() !== ''
                    ? `right after scene group ${after_scene_group_id}`
                    : 'at the start of the video';
            const trimNote = keptWordSpan
                ? ` covering ${Math.round(keptWordSpan.startTimeMs)}ms\u2013${Math.round(keptWordSpan.endTimeMs)}ms of the recording`
                : '';
            return `Success: Added new voiceover scene group ${shortId} ${placement} from uploaded audio${trimNote}.${durationInfo}${newSceneGroupBaseSceneNote()}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding voiceover scene group: ${errorMessage}`;
        }
    }
}
